<template>
    <div class="dw-10 ath-row dashboard-row br-m"
        :qa="`dashboard-row-${index}`"
        :draggable="hoveringDragButton && editMode"
        @drop.prevent="onDrop"
        @dragstart="onDragStart"
        @dragenter="onDragEnter"
        @dragover.prevent
        @dragleave.prevent>
        <div class="dw-10 f drag-drop-line" v-if="isDragHovered">
            <div class="br-m line-color left-dot"></div>
            <div class="line-color fg center-line"></div>
            <div class="br-m line-color right-dot"></div>
        </div>
        <div v-if="header_visible || editMode"
            class="dashboard-header f dw-10 sm-b-l sm-t-l fa-c"
            :qa="`dashboard-row-header-${index}`">
            <!-- Drag and drop button -->
            <FakeButtonIcon v-if="editMode"
                class="el--s-m sm-r-s c-el-panel-bg bf"
                icon="move"
                @mouseover.native="hoveringDragButton = true"
                @mouseleave.native="hoveringDragButton = false" />
            <!-- Name / title for section -->
            <div class="te-fat-light" v-if="!name_edit_mode">{{ title }}</div>
            <!-- Rename button and type badge -->
            <template v-if="canRename && editMode && !name_edit_mode">
                <ButtonIcon class="el--s-s sm-l-s"
                    :qa="`btn-rename-row-${index}`"
                    icon="pencil"
                    @click="onStartRename()" />
                <Badge class="fa-c sm-l-s" :lbl="sectionTypeLabel" />
            </template>
            <!-- Renaming controls -->
            <template v-if="name_edit_mode">
                <FormInput class="fg sm-r-s" v-model="title" :qa="`rename-row-input-${index}`" />
                <ButtonIcon class="sm-r-s"
                    icon="check"
                    @click="onSaveName"
                    title="mead.save_changes"
                    :key="'btn-check-' + panel.uid"
                    :qa="`rename-row-save-${index}`" />
                <ButtonIcon icon="cross"
                    @click="onCancelNameEdit"
                    title="mead.cancel"
                    :key="'btn-cancel-' + panel.uid"
                    :qa="`rename-row-cancel-${index}`" />
            </template>
            <!-- Spacing divider line -->
            <div class="title-divider b-b fg" v-if="editMode"></div>
            <!-- Child limit badge -->
            <Badge class="fa-c sm-l-l" v-if="editMode" :lbl="childLimitBadge" />
            <template v-if="showDevFeatures">
                <!-- Hide Row Header -->
                <ButtonIcon v-if="editMode"
                    class="el--s-m sm-l-s"
                    :icon="header_visible ? 'eye' : 'eye-crossed'"
                    :class="[header_visible ? 'el--t-primary' : 'el--t-light']"
                    :qa="`toggle-row-visible-${index}`"
                    @click="onToggleVisibility()" />
            </template>
            <!-- Add child button  -->
            <ButtonIcon v-if="editMode"
                :qa="`add-child-button-${index}`"
                :disabled="atChildLimit"
                :title="uiBtnTitle"
                class="el--s-m sm-l-s bf el--t-primary"
                icon="plus"
                @click="onShowAddWidgetModal()" />
            <!-- Remove row button -->
            <ButtonIcon v-if="editMode"
                :qa="`remove-row-button-${index}`"
                class="el--s-m sm-l-s"
                icon="cross"
                @click="onShowDeleteModal()" />
        </div>
        <!-- Spacer if we have no header -->
        <div v-else class="dw-10 sm-t-l"></div>
        <!-- Grid mode lays the items out in a CSS grid, flex mode renders them straight into the ath-row -->
        <div :class="isGridLayout ? 'dw-10 dashboard-grid' : 'dashboard-items'">
            <DashboardItem v-for="child in panel.cols"
                :key="child.uid"
                :gridLayout="isGridLayout"
                :rowSpan="child.options && child.options.row_span"
                :colSpan="child.options && child.options.col_span"
                :uid="child.uid"
                :parentUid="panel.uid"
                :hoveredChildUid="hoveredChildUid"
                :draggedChildUid="draggedChildUid"
                :draggedChildPanelUid="draggedChildPanelUid"
                :editMode="editMode"
                :type="child.widget"
                :dashboardType="dashboardType"
                :dashboard="dashboard"
                :childProps="child.options"
                :atChildLimit="atChildLimit"
                @onDrop="onChildDrop"
                @onDragStart="onChildDragStart"
                @onDragEnter="onChildDragEnter"
                @onRemoveItem="onRemoveChildItem"
                @onUpdateItem="onUpdateChildItem"
                @onCopyItem="onCopyItem">
                <template #child>
                    <comp v-bind="child.options"
                        :preview="editMode"
                        :widgetType="child.widget"
                        :dashboardType="dashboardType"
                        :dashboard="dashboard"
                        :is="getWidget(child.widget)" />
                </template>
            </DashboardItem>
        </div>
        <AddEditModal v-if="showAddEditModal"
            :dashboardType="dashboardType"
            :dashboard="dashboard"
            :rowType="panel.type"
            :availableWeight="availableWeight"
            @confirm="onAddChildWidget"
            @close="onCloseAddEditModal" />
        <DeleteModal v-if="showDeleteModal"
            type="section"
            warningText="mead.delete_section_warning"
            @confirm="onRemoveRow"
            @close="onCloseDeleteModal" />
    </div>
</template>
<script>
import { isArray, isBoolean, isString } from '@aretheyhappy/utils';
import UIMobile from '../../../mixins/UIMobile';
import FakeButtonIcon from '../../atoms/FakeButtonIcon.vue';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import Badge from '../../atoms/visuals/Badge.vue';
import FormInput from '../../atoms/forms/FormInput.vue';
import DashboardItem from './DashboardItem.vue';
import AddEditModal from './modals/DashboardAddEditModal.vue';
import DeleteModal from './modals/DashboardDeleteWidgetModal.vue';

export default {
    mixins: [UIMobile],
    components: {DashboardItem, AddEditModal, DeleteModal, FakeButtonIcon, ButtonIcon, Badge, FormInput},
    props: {
        /**
         * Type of dashboard we are working with. Used to look up the
         * dashboard definition (e.g. for rename permissions) and passed
         * through to child items and modals.
         */
        dashboardType: {type: String, default: ''},
        /**
         * Stored panel config for this row, including its uid, type, title,
         * header visibility, and child column definitions.
         */
        panel: {type: Object, default: () => {}},
        /**
         * If we are in edit mode or not.
         */
        editMode: {type: Boolean, default: false},
        /**
         * Uid of panel being dragged during drag and drop. Used to
         * distinguish row-level drags from child-level drags.
         */
        draggedPanelUid: {type: String, default: ''},
        /**
         * Current panel being hovered for drag/drop, used to determine if
         * this row is the current drop target.
         */
        hoveredPanelUid: {type: String, default: ''},
        /**
         * Current child being dragged for drag/drop. Passed down to this
         * row's child DashboardItem components to track drag state.
         */
        draggedChildUid: {type: String, default: ''},
        /**
         * Current child being hovered for drag/drop. Passed down to this
         * row's child DashboardItem components.
         */
        hoveredChildUid: {type: String, default: ''},
        /**
         * Uid of parent uid for dragged child. Passed down to this row's
         * child DashboardItem components to scope drag-and-drop events.
         */
        draggedChildPanelUid: {type: String, default: ''},
        /**
         * Weight that can be added still in the dashboard. Used to
         * determine whether additional widgets can be added to this row.
         */
        availableWeight: {type: Number, default: () => 0},
        /**
         * If new pages can be added.
         */
        canAddPages: {type: Boolean, default: false},
        /**
         * If dev features can be shown.
         */
        showDevFeatures: {type: Boolean, default: false},
        /**
         * Entire Dashboard object.
         */
        dashboard: {type: Object, default: () => ({})},
        /**
         * Index number of the row on the dashboard.
         */
        index: {type: [Number, String], default: ''},
        /**
         * How widgets are laid out inside this row. `flex` uses the flexboxgrid
         * col classes, `grid` uses a 12 column CSS grid that allows widgets to
         * span 1 - 4 rows.
         */
        layoutMode: {type: String, default: 'flex'},
    },
    data: function () {
        return {
            title: '',
            hoveringDragButton: false,
            showAddEditModal: false,
            showDeleteModal: false,
            name_edit_mode: false,
            header_visible: isBoolean(this.panel.header_visible) ? this.panel.header_visible : true,
        };
    },
    methods: {
        getWidget: function (name) {
            return MeadCommon.DashboardService.getDashboardWidgetComponent(name);
        },
        /**
         * Triggered when drop event happens on hovered panel
         */
        onDrop: function () {
            // Ignore if there is no current dragged panel. means we are dragging a child
            if (this.draggedPanelUid.length === 0) return;
            this.$emit('onDrop');
        },
        /**
         * User initiated drag from the drag handle
         */
        onDragStart: function () {
            // If we are not hovering the drag button, dont allow drag and drop
            if (!this.hoveringDragButton) return;
            this.$emit('onDragStart', this.panel.uid);
        },
        /**
         * When we are in drag/drop mode and this panel is entered
         */
        onDragEnter: function () {
            // Ignore if there is no current dragged panel. means we are dragging a child
            if (this.draggedPanelUid.length === 0) return;
            this.$emit('onDragEnter', this.panel.uid);
        },
        onChildDrop: function () {
            this.$emit('onChildDrop', this.panel.uid);
        },
        onChildDragStart: function (uid) {
            this.$emit('onChildDragStart', this.panel.uid, uid);
        },
        onChildDragEnter: function (uid) {
            this.$emit('onChildDragEnter', this.panel.uid, uid);
        },
        onRemoveChildItem: function (uid) {
            this.$emit('onRemoveChildItem', this.panel.uid, uid);
        },
        onUpdateChildItem: function (uid, options) {
            this.$emit('onUpdateChildItem', this.panel.uid, uid, options);
        },
        onCopyItem: function (uid) {
            this.$emit('onCopyChildItem', this.panel.uid, uid);
        },
        /**
         * Show the delete row modal
         */
        onShowDeleteModal: function () {
            // If section is empty remove it immediately
            if (this.panel.cols.length === 0) return this.onRemoveRow();

            this.showDeleteModal = true;
        },
        /**
         * Close the delete row modal
         */
        onCloseDeleteModal: function () {
            this.showDeleteModal = false;
        },
        onRemoveRow: function () {
            this.$emit('onRemoveRow', this.panel.uid);
        },
        /**
         * Show the add widget modal in the blank state
         */
        onShowAddWidgetModal: function () {
            // Row only has one child
            if (this.rowDefinition.getAccessibleChildren().length === 1) {
                const child_type = this.rowDefinition.getAccessibleChildren()[0];
                const child_definition = MeadCommon.DashboardService.getWidgetDefinition(this.rowDefinition.getAccessibleChildren()[0]);
                // Child widget has no config step
                if (child_definition && !child_definition.hasEditConfiguration) {
                    this.onAddChildWidget({
                        type: child_type,
                        options: child_definition.getDefaultProps(),
                    })
                    return;
                }
            }
            this.showAddEditModal = true;
        },
        /**
         * Close AddEditModal without saving changes
         */
        onCloseAddEditModal: function () {
            this.showAddEditModal = false;
        },
        /**
         * When a selection is sent from the add widget flow
         * @param options - {type, options}
         */
        onAddChildWidget: function (options) {
            this.$emit('onAddChildItem', this.panel.uid, options);
        },
        /**
         * Initiate rename mode
         */
        onStartRename: function () {
            this.name_edit_mode = true;
        },
        /**
         * Inform parent of change
         */
        onSaveName: function () {
            this.name_edit_mode = false;
            this.$emit('onRowUpdated', this.panel.uid, {
                title: this.title,
            });
        },
        /**
         * Cancel name editing
         */
        onCancelNameEdit: function () {
            this.name_edit_mode = false;
            this.title = this.panel.title || this.rowDefinition.getLabel();
        },
        /**
         * Toggle visibility of this row
         */
        onToggleVisibility: function () {
            this.header_visible = !this.header_visible;
            this.$emit('onRowUpdated', this.panel.uid, {
                header_visible: this.header_visible,
            });
        }
    },
    computed: {
        /**
         * If this row is using the CSS grid layout
         */
        isGridLayout: function () {
            return this.layoutMode === 'grid';
        },
        rowDefinition: function () {
            return MeadCommon.DashboardService.getRowDefinition(this.panel.type);
        },
        /**
         * Number of children this panel currently has
         */
        childCount: function () {
            return isArray(this.panel.cols) ? this.panel.cols.length : 0;
        },
        /**
         * If we are currently at the limit of the number of children for this panel type
         */
        atChildLimit: function () {
            return ((isArray(this.panel.cols) ? this.panel.cols.length : 0) === this.maximumChildren) || !this.canAddWidgets;
        },
        /**
         * Check if the section has at least one widget with weight not bigger then the available weight
         */
        canAddWidgets: function () {
            let can_add = false;

            for (const el of this.rowDefinition.getAccessibleChildren()) {
                const widget = MeadCommon.DashboardService.getWidgetDefinition(el);
                if (widget && widget.getWeight() <= this.availableWeight) {
                    can_add = true;

                    break;
                }
            }

            return can_add;
        },
        /**
         * Maximum children allowed for this panel type
         */
        maximumChildren: function () {
            return this.rowDefinition.getLimit();
        },
        /**
         * Badge text to show in edit mode for the count of children over max
         */
        childLimitBadge: function () {
            return `${this.childCount}/${this.maximumChildren}`;
        },
        /**
         * If this panel is currently the target of drag and drop
         */
        isDragHovered: function () {
            return this.hoveredPanelUid === this.panel.uid;
        },
        /**
         * If this row can be renamed.
         */
        canRename: function () {
            return MeadCommon.DashboardService.getDashboardDefinition(this.dashboardType).canRenameRows();
        },
        /**
         * Returns original section type label
         */
        sectionTypeLabel: function () {
            return this.rowDefinition.getLabel();
        },
        /**
         * Returns the button title
         */
        uiBtnTitle: function () {
            if (!this.atChildLimit) return false;

            return this.canAddPages ? 'mead.widget_limit_reached_new_page_available' : 'mead.widget_limit_reached';
        },
    },
    created: function () {
        if (!isString(this.panel.type)) {
            console.error('DashboardRow requires a type to be present on the panel to render correctly');
            return;
        }
        this.title = this.panel.title || this.rowDefinition.getLabel();
    },
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/variables';
.title-divider {
    height: 2rem;
    margin: 0 0 2rem 2rem;
}

.te-fat-light {
    align-content: center;
}

.line-color {
    background-color: #333333;
}

:root[ath-theme="dark"] {
    .line-color {
        background-color: #FFFFFF;
    }
}

.dev-button {
    position: relative;

    .dev-badge {
        position: absolute;
        right: -1.5rem;
        top: -1rem;
    }
}

// Flex mode wrapper, removed from layout so items stay direct flex children of the ath-row
.dashboard-items {
    display: contents;
}

// CSS grid layout for `layoutMode="grid"`. Widgets keep their flexboxgrid size classes,
// which are mapped onto a 12 column grid here. Items can also span 2 - 4 rows
// (`.dashboard-item--span-N`) so a tall widget can sit beside several stacked widgets.
// Note: `-min-` classes do not grow to fill the row in this mode, they are treated
// as a fixed column span.
@mixin dashboard-grid-spans($bp) {
    @for $i from 1 through 12 {
        > .col-#{$bp}-#{$i},
        > .col-#{$bp}-min-#{$i} {
            grid-column: span $i;
        }
    }
}

.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    // Every grid row is at least 10rem tall so `row_span` gives predictable heights
    // (a 2 row widget is at least 20rem plus spacing) instead of rows being sized only
    // by their content. Rows still grow past this when a widget's content or min-height
    // needs more room.
    grid-auto-rows: minmax(10rem, auto);
    grid-auto-flow: row;

    > .dashboard-item {
        box-sizing: border-box;
        flex: none;
        min-width: 0;
        max-width: none;
        padding-right: 0.5rem;
        padding-left: 0.5rem;
        grid-column: span 12;

        // Stretch the widget to fill its grid area so tall widgets match the stacked ones beside them
        display: flex;
        flex-direction: column;

        > :not(.drag-buttons):not(.drag-drop-line) {
            flex-grow: 1;
        }
    }

    // Extra Small layouts
    @include dashboard-grid-spans(xs);
}

// Small layouts
@media screen and (min-width: $mq-m), print and (min-width: $mq-m) {
    .dashboard-grid {
        @include dashboard-grid-spans(sm);
    }
}

// Medium layouts
@media screen and (min-width: $mq-ds), print and (min-width: $mq-ds) {
    .dashboard-grid {
        @include dashboard-grid-spans(md);

        // Only span rows once widgets sit side by side, on smaller screens everything stacks
        @for $i from 2 through 4 {
            > .dashboard-item--span-#{$i} {
                grid-row: span $i;
            }
        }
    }
}

// Large layouts
@media screen and (min-width: $mq-dm), print and (min-width: $mq-dm) {
    .dashboard-grid {
        @include dashboard-grid-spans(lg);
    }
}

// Custom widths (`options.col_span`) override the size classes from md up. Kept after
// the lg block so they win over the lg size classes too, below md widgets stack as normal.
@media screen and (min-width: $mq-ds), print and (min-width: $mq-ds) {
    .dashboard-grid {
        @for $i from 1 through 12 {
            > .dashboard-item--cols-#{$i} {
                grid-column: span $i;
            }
        }
    }
}

.dashboard-row {
    // This removes the parent background color when doing drag and drop
    &[draggable="true"] {
        position: relative;
        z-index: 1;
    }
}

.drag-drop-line {
    position: relative;

    .left-dot {
        width: 1rem;
        height: 1rem;
        position: absolute;
        left: -.8rem;
    }

    .center-line {
        height: .5rem;
        margin-top: 0.25rem;
    }

    .right-dot {
        width: 1rem;
        height: 1rem;
        position: absolute;
        right: -.8rem;
    }
}
</style>